import React, { useEffect, useLayoutEffect, useReducer, useRef } from 'react';
import { ensureSpriteSymbol, getSpriteSymbol } from './svgSpriteRegistry';
import type { SpriteSymbol } from './svgSpriteRegistry';
import styles from './SpriteSvg.module.css';

/**
 * Resolves `url` to a sprite symbol. Returns `undefined` while the first fetch of that
 * URL is in flight, `null` when the SVG could not be inlined (caller falls back to
 * `<img>`), and the symbol otherwise. Already-settled URLs resolve synchronously, so
 * re-mounts (virtualised rows, view-mode switches) never flash.
 */
export const useSpriteSymbol = (url: string): SpriteSymbol | null | undefined => {
  const [, rerender] = useReducer((n: number) => n + 1, 0);
  const known = url ? getSpriteSymbol(url) : null;

  useEffect(() => {
    if (!url || getSpriteSymbol(url) !== undefined) return undefined;
    let alive = true;
    void ensureSpriteSymbol(url).then(() => {
      if (alive) rerender();
    });
    return () => {
      alive = false;
    };
  }, [url]);

  return known;
};

type DataAttributes = Record<`data-${string}`, unknown>;

interface SpriteSvgProps {
  symbol: SpriteSymbol;
  className?: string;
  style?: React.CSSProperties;
  /** Mirrors `<img alt>`: empty/absent → decorative (`aria-hidden`), otherwise `role="img"` + `aria-label`. */
  alt?: string;
  id?: string;
  onClick?: React.MouseEventHandler<SVGSVGElement>;
  /** Fires once per mounted symbol — the sprite equivalent of `<img onLoad>`. */
  onReady?: () => void;
  dataAttributes?: DataAttributes;
}

/**
 * How an <img> with this `object-fit` fits an SVG into its CSS box, as a preserveAspectRatio for
 * the sprite <svg>. `fill` (the img default) sizes the SVG viewport to the box and leaves the fit
 * to the source root's own preserveAspectRatio, so that value is used rather than `none`.
 */
const aspectRatioFor = (objectFit: string, symbol: SpriteSymbol): string => {
  if (objectFit === 'contain' || objectFit === 'scale-down') return 'xMidYMid meet';
  if (objectFit === 'cover') return 'xMidYMid slice';
  return symbol.preserveAspectRatio;
};

// Sprites mounted in one commit read their object-fit together, after the commit's layout effects have run
// and before the browser paints: one style recalc for the whole batch instead of one per sprite (a row of cards
// mounting during fast scroll was paying a forced style recalc per icon).
const pendingAspectRatios = new Map<SVGSVGElement, SpriteSymbol>();
let aspectRatioFlushQueued = false;

const flushAspectRatios = () => {
  aspectRatioFlushQueued = false;
  const batch = Array.from(pendingAspectRatios);
  pendingAspectRatios.clear();

  const objectFits = batch.map(([el]) => (el.isConnected ? window.getComputedStyle(el).objectFit : null));
  batch.forEach(([el, symbol], index) => {
    const objectFit = objectFits[index];
    if (objectFit !== null) el.setAttribute('preserveAspectRatio', aspectRatioFor(objectFit, symbol));
  });
};

const scheduleAspectRatio = (el: SVGSVGElement, symbol: SpriteSymbol) => {
  pendingAspectRatios.set(el, symbol);
  if (aspectRatioFlushQueued) return;
  aspectRatioFlushQueued = true;
  queueMicrotask(flushAspectRatios);
};

/**
 * `<svg viewBox><use href="#symbol" /></svg>` sized like the `<img>` it replaces: intrinsic
 * width/height attributes from the source SVG, the global `img` defaults
 * (`display:block; max-width:100%; height:auto`) via the `.sprite` class, and the site's
 * `object-fit` (read once from computed style) mapped onto preserveAspectRatio so a box
 * whose ratio differs from the source letterboxes / stretches exactly as the <img> did.
 */
const SpriteSvg: React.FC<SpriteSvgProps> = ({
  symbol,
  className,
  style,
  alt,
  id,
  onClick,
  onReady,
  dataAttributes,
}) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;

  useLayoutEffect(() => {
    const el = svgRef.current;
    if (el) scheduleAspectRatio(el, symbol);
  }, [symbol]);

  useEffect(() => {
    onReadyRef.current?.();
  }, [symbol.id]);

  const decorative = !alt;

  return (
    <svg
      {...dataAttributes}
      ref={svgRef}
      data-sprite=""
      data-loaded=""
      id={id}
      className={className ? `${styles.sprite} ${className}` : styles.sprite}
      style={style}
      viewBox={symbol.viewBox}
      width={symbol.width}
      height={symbol.height}
      role={decorative ? undefined : 'img'}
      aria-label={decorative ? undefined : alt}
      aria-hidden={decorative ? true : undefined}
      focusable="false"
      onClick={onClick}
    >
      <use href={`#${symbol.id}`} />
    </svg>
  );
};

export default SpriteSvg;
